import { Field, NumberInput, SingleSelectDropdown, TextInput } from "@carbon6/c6-frontend-design";
import { useEffect, useState, type ReactNode } from "react";
import {
  createTrainingGpuQuotaRequest,
  TRAINING_GPU_QUOTA_REQUEST_DAYS,
  type TrainingGpuQuotaRequestDays,
} from "@/api";
import { hatcheryDropdownModes } from "@/app/theme";
import { HatTextModal } from "@/components/modal";
import { isHttpClientError } from "@/http";
import { formatGpuLimit, formatOptionalGpuCount } from "./gpuQuotaFormat";
import styles from "./GpuQuotaPage.module.css";

// 요청 대상. 지금 한도와 group 상한을 함께 받아 폼 안에 근거로 표시한다. 사용자가 요청 화면에서
// 표로 다시 돌아가지 않아도 된다.
export type GpuQuotaRequestTarget = {
  workspaceId: string;
  workspaceName: string;
  effectiveGpuLimit: number | null;
  gpuLimit: number | null;
  groupGpuMaxLimit: number | null;
};

// 사유 상한은 서버 스키마(1000자)와 같다. 넘으면 422 이므로 입력 단계에서 막는다.
const reasonMaxLength = 1000;

// 증액 기간의 기본값. 가장 짧은 기간이다.
const defaultRequestedDays: TrainingGpuQuotaRequestDays = TRAINING_GPU_QUOTA_REQUEST_DAYS[0];

// 드롭다운은 문자열 값을 쓴다. 선택지는 API 계약의 네 값에서 만든다.
const requestedDaysOptions = TRAINING_GPU_QUOTA_REQUEST_DAYS.map((days) => ({
  value: String(days),
  label: `${days}일`,
}));

// 한도 상향 요청 폼. 요청은 기간이 있는 임시 증액이다. 기준 한도·timezone·schedule 은 받지 않는다.
// 증액은 그 값을 바꾸지 않고 스케줄 위에 얹힌다.
export function GpuQuotaRequestModal({
  target,
  onClose,
  onSubmitted,
}: {
  // null 이면 닫힌 상태.
  target: GpuQuotaRequestTarget | null;
  onClose: () => void;
  onSubmitted: () => void;
}): ReactNode {
  const [requestedLimit, setRequestedLimit] = useState<number | null>(null);
  const [requestedDays, setRequestedDays] =
    useState<TrainingGpuQuotaRequestDays>(defaultRequestedDays);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 대상이 바뀌면 폼을 초기화한다. 이전 workspace 의 입력이 남아 있으면 잘못된 요청이 된다.
  useEffect(() => {
    setRequestedLimit(target?.effectiveGpuLimit ?? null);
    setRequestedDays(defaultRequestedDays);
    setReason("");
    setErrorMessage(null);
    setBusy(false);
  }, [target]);

  const canSubmit = requestedLimit !== null && requestedLimit >= 1 && reason.trim().length > 0;

  const handleSubmit = (): void => {
    if (target === null || !canSubmit || busy) {
      return;
    }
    setBusy(true);
    setErrorMessage(null);
    void createTrainingGpuQuotaRequest({
      trainingWorkspaceId: target.workspaceId,
      requestedGpuLimit: requestedLimit,
      requestedDays,
      reason: reason.trim(),
    })
      .then(() => {
        onSubmitted();
        onClose();
      })
      .catch((error: unknown) => {
        setErrorMessage(resolveCreateErrorMessage(error));
        setBusy(false);
      });
  };

  return (
    <HatTextModal
      open={target !== null}
      label="GPU 한도 상향을 요청합니다."
      eyebrow={target?.workspaceName}
      caption="관리자가 승인하면 그때부터 고른 기간 동안 한도가 올라갑니다. 기간이 끝나면 원래 한도로 돌아갑니다. 요일·시간대 스케줄은 그대로 유지됩니다."
      okText="요청"
      cancelText="취소"
      busy={busy}
      okDisabled={!canSubmit}
      error={errorMessage !== null}
      errorMessage={errorMessage ?? undefined}
      onOkPress={handleSubmit}
      onCancelPress={onClose}
      content={
        target === null ? null : (
          <div className={styles.requestForm}>
            <dl className={styles.requestFacts}>
              <div className={styles.requestFact}>
                <dt>지금 한도</dt>
                <dd>{formatGpuLimit(target.effectiveGpuLimit, target.gpuLimit)}</dd>
              </div>
              <div className={styles.requestFact}>
                <dt>그룹 상한</dt>
                <dd>{formatOptionalGpuCount(target.groupGpuMaxLimit)}</dd>
              </div>
            </dl>
            <Field label="희망 한도 (GPU)">
              <NumberInput
                value={requestedLimit}
                onChange={(value) => setRequestedLimit(value)}
                min={1}
                max={10000}
                maxDecimalPlaces={0}
              />
            </Field>
            <Field label="증액 기간">
              <SingleSelectDropdown
                mode={hatcheryDropdownModes.default}
                label="증액 기간"
                fullWidth
                menuWidth="matchTrigger"
                value={String(requestedDays)}
                options={requestedDaysOptions}
                onValueChange={(value) => {
                  const next = TRAINING_GPU_QUOTA_REQUEST_DAYS.find(
                    (days) => String(days) === value,
                  );
                  if (next !== undefined) {
                    setRequestedDays(next);
                  }
                }}
              />
            </Field>
            <Field label="사유">
              <TextInput
                value={reason}
                maxLength={reasonMaxLength}
                placeholder="예) 다음 주 대규모 Training 예정"
                onChange={(event) => setReason(event.target.value)}
              />
            </Field>
            {/* 증액은 지금 한도와 비교해 큰 쪽이 적용된다. 크지 않은 값은 승인돼도 효과가 없다. */}
            {target.effectiveGpuLimit !== null &&
            requestedLimit !== null &&
            requestedLimit <= target.effectiveGpuLimit ? (
              <p className={styles.requestWarning} role="status">
                지금 한도보다 크지 않은 값입니다. 승인되어도 지금 시간대의 한도는 그대로입니다.
              </p>
            ) : null}
            {/* 상한을 넘어도 접수된다. 서버는 접수할 때 상한을 검증하지 않는다. */}
            {target.groupGpuMaxLimit !== null &&
            requestedLimit !== null &&
            requestedLimit > target.groupGpuMaxLimit ? (
              <p className={styles.requestWarning} role="status">
                그룹 상한보다 큰 값입니다. 접수는 되지만 그룹 상한을 함께 올려야 실제로 적용됩니다.
              </p>
            ) : null}
          </div>
        )
      }
    />
  );
}

/** 접수 실패 문구. 계약된 코드는 다음 행동을 안내한다. */
function resolveCreateErrorMessage(error: unknown): string {
  if (isHttpClientError(error)) {
    switch (error.code) {
      case "quota_request.already_pending":
        return "이 workspace 에 이미 대기 중인 요청이 있습니다. 먼저 철회해 주세요.";
      case "quota_request.forbidden":
        return "이 workspace 를 선점한 사람만 요청할 수 있습니다.";
    }
  }
  return "요청을 접수하지 못했습니다.";
}
